export function parseGroupedStatistics(data, valueField = "statisticsSum") {
  if (!Array.isArray(data) || !data.length) {
    return { groupField: [], categoriesData: [] };
  }

  const groupField = data.map((group) => ({
    name: group.name || "",
    statisticsModels: Array.isArray(group.statisticsModels)
      ? group.statisticsModels
      : [],
  }));

  const categories = new Set();
  groupField.forEach((group) => {
    group.statisticsModels.forEach((item) => {
      if (item && item.name) {
        categories.add(item.name);
      }
    });
  });

  const categoriesData = Array.from(categories).map((category) => ({
    name: category,
  }));

  categoriesData.forEach((row) => {
    groupField.forEach((group, index) => {
      const item = group.statisticsModels.find((model) => model.name === row.name);
      row[`group${index}`] = item ? item[valueField] || 0 : 0;
    });
  });

  return { groupField, categoriesData };
}

export async function runStatisticsRequest(
  vm,
  requestFn,
  params,
  { errorMessage = "获取数据失败", emptyFallback = [], onSuccess } = {}
) {
  try {
    const { data: res } = await requestFn(params);
    if (res.code === 200) {
      const payload = res.data != null ? res.data : emptyFallback;
      onSuccess(payload);
      return payload;
    }
    vm.$message.error(res.message || errorMessage);
    onSuccess(emptyFallback);
    return emptyFallback;
  } catch (e) {
    vm.$message.error(errorMessage);
    onSuccess(emptyFallback);
    return emptyFallback;
  }
}

export function buildDateText(startDate, type) {
  if (!startDate) {
    return "";
  }
  if (type === "year") {
    return `${startDate.slice(0, 4)}年`;
  }
  if (type === "month") {
    return `${startDate.slice(0, 4)}年${startDate.slice(5, 7)}月`;
  }
  if (type === "date") {
    return `${startDate.slice(0, 4)}年${startDate.slice(5, 7)}月${startDate.slice(8, 10)}日`;
  }
  return "";
}

/** 逐字竖排（一字一行，自上而下） */
export function formatVerticalStackLabel(value) {
  const text = String(value || "");
  if (!text) {
    return "";
  }
  return text.split("").join("\n");
}

/**
 * 柱状图 X 轴自适应布局（逐字竖排 + 底部留白 + 过多类目时缩放）
 * @param {string[]} names 类目名
 * @param {{ narrow?: boolean }} options narrow=true 表示半宽并排图表
 */
export function buildAdaptiveCategoryLayout(names, { narrow = true } = {}) {
  const list = (names || []).map((name) => String(name || ""));
  const count = list.length;
  const maxLen = count ? Math.max(...list.map((name) => name.length)) : 0;
  const fontSize = count > 18 ? 11 : 12;
  const lineHeight = fontSize + 2;

  // 并排半宽图：默认逐字竖排，避免横向挤在一起
  const useCharStack = narrow
    ? count > 0
    : count > 6 || maxLen > 6;

  let bottom = 48;
  const axisLabel = {
    interval: 0,
    fontSize,
    lineHeight,
  };

  if (useCharStack) {
    axisLabel.formatter = (value) => formatVerticalStackLabel(value);
    bottom = Math.min(Math.max(80, maxLen * lineHeight + 32), 360);
  } else if (count > 6) {
    axisLabel.rotate = 35;
    bottom = Math.min(Math.max(76, maxLen * 6 + 44), 150);
  } else {
    bottom = Math.max(48, Math.ceil(maxLen / 6) * 18 + 36);
  }

  const zoomThreshold = narrow ? 10 : 16;
  const visibleCount = narrow ? 8 : 12;
  let dataZoom = [];
  if (count > zoomThreshold) {
    const endPercent = Math.min(100, Math.round((visibleCount / count) * 100));
    const sliderHeight = 22;
    bottom += sliderHeight + 12;
    dataZoom = [
      { type: "inside", start: 0, end: endPercent },
      {
        type: "slider",
        start: 0,
        end: endPercent,
        height: sliderHeight,
        bottom: 8,
        brushSelect: false,
      },
    ];
  }

  return {
    grid: {
      left: 48,
      right: 24,
      top: 36,
      bottom,
      containLabel: !useCharStack,
    },
    dataZoom,
    axisLabel,
  };
}

/** 带自适应 X 轴的柱状图 option */
export function buildAdaptiveBarChartOption(
  names,
  values,
  color,
  { narrow = true, unit = "件" } = {},
) {
  const layout = buildAdaptiveCategoryLayout(names, { narrow });
  return {
    tooltip: {
      trigger: "axis",
      axisPointer: { type: "shadow" },
    },
    grid: layout.grid,
    dataZoom: layout.dataZoom,
    xAxis: {
      type: "category",
      data: names,
      axisLabel: layout.axisLabel,
    },
    yAxis: {
      type: "value",
      name: unit,
    },
    series: [
      {
        name: "",
        data: values,
        type: "bar",
        color,
        label: {
          show: true,
          position: "top",
        },
      },
    ],
  };
}
